import React from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListItemText from '@mui/material/ListItemText';
import { sa, PillButton } from '../../components/superAdminUi';
import emailTemplateVariantService from '../../services/api/emailTemplateVariantService';

// WI-443: editor for a single email template variant. HTML source editing with a
// token palette and a sample-data preview (rendered server-side via POST Preview).

const TokenPalette = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0 16px;
`;

const TokenChip = styled.button`
  border: 1px solid ${sa.color.stroke};
  background: #eef4f4;
  color: ${sa.color.text};
  font-family: ${sa.font.family};
  font-size: 11px;
  font-weight: ${sa.font.bold};
  padding: 3px 10px;
  border-radius: 999px;
  cursor: pointer;
  &:hover {
    background: #dcebeb;
  }
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  min-height: 420px;
  border: 1px solid ${sa.color.stroke};
  border-radius: 4px;
  background: #fff;
`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const Hint = styled.p`
  font-family: ${sa.font.family};
  font-size: 12px;
  color: #777;
  margin: 4px 0 12px;
`;

const emptyForm = (templateKey) => ({
  Id: 0,
  TemplateKey: templateKey,
  Name: '',
  Subject: '',
  Body: '',
  IsDefault: false,
  IsActive: true,
  Products: [],
  OrganisationTypes: [],
  GroupTypes: [],
  Countries: [],
});

export default function EmailTemplateEditor({
  category,
  variant,
  options,
  onClose,
  onSaved,
}) {
  const isNew = !variant;
  const [form, setForm] = React.useState(
    variant ? { ...emptyForm(category.TemplateKey), ...variant } : emptyForm(category.TemplateKey)
  );
  const [tab, setTab] = React.useState('edit'); // 'edit' | 'preview'
  const [preview, setPreview] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const bodyRef = React.useRef(null);

  const tokens = category.Tokens || [];
  const products = (options && options.Products) || [];
  const organisationTypes = (options && options.OrganisationTypes) || [];
  const groupTypes = (options && options.GroupTypes) || [];
  const countries = (options && options.Countries) || [];

  const set = (field) => (event) =>
    setForm((f) => ({ ...f, [field]: event.target.value }));

  const insertToken = (token) => {
    const textarea = bodyRef.current;
    setForm((f) => {
      if (!textarea) return { ...f, Body: (f.Body || '') + token };
      const start = textarea.selectionStart ?? f.Body.length;
      const end = textarea.selectionEnd ?? f.Body.length;
      return {
        ...f,
        Body: f.Body.slice(0, start) + token + f.Body.slice(end),
      };
    });
  };

  const showPreview = async () => {
    const res = await emailTemplateVariantService.previewTemplate({
      TemplateKey: form.TemplateKey,
      Subject: form.Subject,
      Body: form.Body,
    });
    if (res.success === false) {
      toast.error(res.message || 'Preview failed');
      return;
    }
    setPreview(res.data);
    setTab('preview');
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = isNew
        ? await emailTemplateVariantService.addVariant(form)
        : await emailTemplateVariantService.updateVariant(form);
      if (res.success === false) {
        toast.error(res.message || 'Save failed');
        return;
      }
      toast.success(isNew ? 'Template created' : 'Template saved');
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const multiSelect = (label, field, values, optionList, renderValue) => (
    <TextField
      select
      fullWidth
      size='small'
      margin='dense'
      label={label}
      value={values}
      onChange={set(field)}
      SelectProps={{
        multiple: true,
        renderValue: (selected) =>
          selected.map(renderValue || ((v) => v)).join(', '),
      }}
      helperText='Leave empty to match any'
    >
      {optionList.map((option) => {
        const value = typeof option === 'object' ? option.Id : option;
        const text = typeof option === 'object' ? option.Name : option;
        return (
          <MenuItem key={value} value={value}>
            <Checkbox checked={values.indexOf(value) > -1} size='small' />
            <ListItemText primary={text} />
          </MenuItem>
        );
      })}
    </TextField>
  );

  const productName = (id) => {
    const found = products.find((p) => p.Id === id);
    return found ? found.Name : id;
  };

  return (
    <Dialog open fullWidth maxWidth='md' onClose={onClose}>
      <DialogTitle style={{ fontFamily: sa.font.family }}>
        {isNew ? `Add variant — ${category.Label}` : `Edit — ${form.Name}`}
      </DialogTitle>
      <DialogContent dividers>
        <TabRow>
          <PillButton
            type='button'
            $variant={tab === 'edit' ? undefined : 'outline'}
            onClick={() => setTab('edit')}
          >
            Edit
          </PillButton>
          <PillButton type='button' $variant={tab === 'preview' ? undefined : 'outline'} onClick={showPreview}>
            Preview
          </PillButton>
        </TabRow>

        {tab === 'edit' && (
          <>
            <TextField
              fullWidth
              size='small'
              margin='dense'
              label='Name'
              value={form.Name}
              onChange={set('Name')}
            />
            <TextField
              fullWidth
              size='small'
              margin='dense'
              label='Subject'
              value={form.Subject}
              onChange={set('Subject')}
            />
            <Hint>
              Click a placeholder to insert it into the body at the cursor.
              Placeholders are replaced with real values when the email is sent.
            </Hint>
            <TokenPalette>
              {tokens.map((t) => (
                <TokenChip
                  key={t.Token}
                  type='button'
                  title={t.Description}
                  onClick={() => insertToken(t.Token)}
                >
                  {t.Token}
                </TokenChip>
              ))}
            </TokenPalette>
            <TextField
              fullWidth
              multiline
              minRows={12}
              maxRows={24}
              margin='dense'
              label='Body (HTML)'
              value={form.Body}
              onChange={set('Body')}
              inputRef={bodyRef}
              InputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            />
            {!form.IsDefault && (
              <>
                {multiSelect('Products', 'Products', form.Products, products, productName)}
                {multiSelect('Customer types', 'OrganisationTypes', form.OrganisationTypes, organisationTypes)}
                {multiSelect('Group types', 'GroupTypes', form.GroupTypes, groupTypes)}
                {multiSelect('Countries', 'Countries', form.Countries, countries)}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.IsActive}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, IsActive: e.target.checked }))
                      }
                    />
                  }
                  label='Active (used for matching customers)'
                />
              </>
            )}
            {form.IsDefault && (
              <Hint>
                This is the default template for this category — it is used whenever
                no criteria variant matches, so it cannot be deleted or deactivated.
              </Hint>
            )}
          </>
        )}

        {tab === 'preview' && preview && (
          <>
            <Hint>
              Subject: <strong>{preview.Subject}</strong> (sample values)
            </Hint>
            <PreviewFrame title='Email preview' sandbox='' srcDoc={preview.Body} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <PillButton type='button' $variant='outline' onClick={onClose}>
          Cancel
        </PillButton>
        <PillButton type='button' disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </PillButton>
      </DialogActions>
    </Dialog>
  );
}
